import { MuiAlert } from '@fox/atoms';
import { createContext, useContext, useState } from 'react';
import { useContentContext } from './content-context';
import { useSwiftFoxTenantConfig } from './Membership/query-hooks/query-implementations';

export type SwiftFoxTenantConfig = {
	countryCode?: string;
	clientDetails?: SwiftFoxClientDetails;
};

type SwiftFoxClientDetails = {
	companyName?: string;
	companyDisplayPhoto?: string;
	companyEmail?: string;
	companyMobile?: string;
	companyAddress?: string;
	companyPhone?: string;
	companyAbn?: string;
	companyWebsite?: string;
};

type SwiftFoxTenantConfigContext = SwiftFoxTenantConfig & {
	loading: boolean;
	error?: string;
};

const Context = createContext<SwiftFoxTenantConfigContext | undefined>(undefined);
Context.displayName = 'SwiftFoxTenantConfigContext';

/**
 * Provider component to fetch and provide SwiftFox tenant configuration.
 * This context is used to share tenant configuration data across components.
 * For the moment we only use the country code, but this can be extended in the future.
 * Using a ref to store the configuration allows us to avoid unnecessary re-renders
 */
export function SwiftFoxTenantConfigProvider({ children, enabled }: { children: React.ReactNode; enabled: boolean }) {
	const { contentId, requestToken } = useContentContext();

	const [swiftFoxTenantConfig, setSwiftFoxTenantConfig] = useState<SwiftFoxTenantConfig | undefined>(undefined);

	const { error, loading } = useSwiftFoxTenantConfig({
		contentId,
		requestToken,
		onFetchComplete: (data: SwiftFoxTenantConfig) => {
			setSwiftFoxTenantConfig(data);
			window['__i18n'] = { countryCode: data.countryCode };
		},
		skip: !enabled
	});

	const contextValue: SwiftFoxTenantConfigContext = {
		...swiftFoxTenantConfig,
		loading,
		error
	};

	if (enabled && !swiftFoxTenantConfig) {
		return <></>;
	}

	if (error) {
		return <MuiAlert severity="error">{error}</MuiAlert>;
	}

	return <Context.Provider value={contextValue}>{children}</Context.Provider>;
}

//we are not actually using this yet, but it is here for possible future use
export function useSwiftFoxTenantConfigContext() {
	const context = useContext(Context);

	if (!context) throw new Error('Missing SwiftFox tenant config context');

	return context;
}
